// Voucher API functions
import { getUserInfo } from './auth';
import { CommonUtils } from './commonUtils';
import { getApiV1Url } from '../config/api';
import { resolveStoredFileUrl, toCdnCacheVersion, type UploadFilePayload } from './storage';
import { generateAfter } from './fractionalIndex';

// ============================================================================
// INTERFACES
// ============================================================================

// 기본 교환권 정보
export interface Voucher {
  projectCode: string;
  brandCode?: string;
  productCode?: string;
  code: string;
  masterCode: string;
  status: string;
  type: string;
  name: string;
  price: number;
  balance: number;
  discountTarget?: string;
  discountType?: string;
  discountValue?: number;
  maxDiscount?: number;
  minPayment?: number;
  validType: number;
  validPeriod?: number;
  validUntil?: string;
  issueLimit: number;
  memberLimit?: number;
  comment?: string;
  description?: string;
  notice?: string;
  /** 대표 파일 버전 (CDN `?v=` 우선 사용, API Long / DB bigint) */
  primaryVersion?: number | null;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
}

// 교환권 상세 정보 (Voucher와 동일)
export interface VoucherDetail extends Voucher {}

/** 교환권 상세 API 응답 전용 필드 (폼/업데이트 대상 아님) */
export interface VoucherDetailResponse extends VoucherDetail {
  /** 교환권이 속한 트리 코드 목록 (그룹코드:노드코드) */
  treeCodes?: string[];
  /** 트리별 정렬 정보 (treeCode -> fractional index) */
  treeSortInfo?: Record<string, string>;
  brandName?: string;
}

// 교환권 생성 요청
export interface VoucherCreateRequest {
  brandCode?: string;
  productCode: string | null;
  masterCode: string;
  status: string;
  type: string;
  name: string;
  price: number;
  balance: number;
  discountTarget: string;
  discountType: string;
  discountValue: number;
  maxDiscount?: number;
  minPayment?: number;
  validType: number;
  validPeriod?: number;
  validUntil?: string;
  issueLimit: number;
  memberLimit?: number;
  comment?: string;
  description?: string;
  notice?: string;
}

// 교환권 수정 요청
export interface VoucherUpdateRequest {
  brandCode?: string;
  productCode: string | null;
  masterCode: string;
  status: string;
  type: string;
  name: string;
  price: number;
  balance: number;
  discountTarget?: string;
  discountType?: string;
  discountValue?: number;
  maxDiscount?: number;
  minPayment?: number;
  validType: number;
  validPeriod?: number;
  validUntil?: string;
  issueLimit: number;
  memberLimit?: number;
  comment?: string;
  description?: string;
  notice?: string;
}

// 교환권 목록 요청
export interface VoucherListRequest {
  page?: number;
  size?: number;
  sort?: string;
  status?: string;
  type?: string;
  search?: string;
  brandCode?: string;
}

// 교환권 목록 응답
export interface VoucherListResponse {
  totalCount: number;
  vouchers: Voucher[];
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

// 교환권 생성 응답
export interface VoucherCreateResponse {
  success: boolean;
  message: string;
  data?: {
    voucherCode: string;
  };
}

// 교환권 수정 응답
export interface VoucherUpdateResponse {
  success: boolean;
  message: string;
  data?: {
    voucherCode: string;
  };
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

// 교환권 생성 API
export const createVoucher = async (
  projectCode: string, 
  voucherData: VoucherCreateRequest
): Promise<VoucherCreateResponse> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
    };

    const response = await fetch(
      getApiV1Url(`/projects/${projectCode}/vouchers`),
      {
        method: 'POST',
        headers,
        body: JSON.stringify(voucherData)
      }
    );

    if (!response.ok) {
      const statusCode = response.status;
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = CommonUtils.getHttpErrorMessage(statusCode, errorData);
      throw new Error(errorMessage);
    }

    const result = await response.json();
    return {
      success: true,
      message: result.message || '교환권이 성공적으로 생성되었습니다.',
      data: result.data
    };
  } catch (error: any) {
    console.error('교환권 생성 API 오류:', error);
    
    // 네트워크 에러인 경우 사용자 친화적 메시지로 변환
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }
    
    throw error;
  }
};

// 교환권 목록 조회 API
export const getVoucherList = async (
  projectCode: string,
  params: VoucherListRequest = {}
): Promise<VoucherListResponse> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
    };

    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.size) queryParams.append('size', params.size.toString());
    if (params.sort) queryParams.append('sort', params.sort);
    if (params.status) queryParams.append('status', params.status);
    if (params.type) queryParams.append('type', params.type);
    if (params.search) queryParams.append('search', params.search);
    if (params.brandCode) queryParams.append('brandCode', params.brandCode);

    const response = await fetch(
      `${getApiV1Url(`/projects/${projectCode}/vouchers`)}?${queryParams.toString()}`,
      {
        method: 'GET',
        headers
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('교환권 목록 서버 응답:', result);
    console.log('서버 응답 data 필드:', result.data);
    
    if (result.data) {
      return {
        totalCount: result.data.totalCount,
        vouchers: result.data.vouchers || [],
        totalPages: result.data.totalPages,
        currentPage: result.data.currentPage,
        pageSize: result.data.pageSize,
        hasNext: result.data.hasNext,
        hasPrevious: result.data.hasPrevious
      };
    }
    
    return result;
  } catch (error: any) {
    console.error('교환권 목록 조회 중 오류 발생:', error);
    
    // 네트워크 에러인 경우 사용자 친화적 메시지로 변환
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }
    
    throw error;
  }
};

// 교환권 상세 조회 API
export const getVoucherDetail = async (
  projectCode: string,
  voucherCode: string
): Promise<VoucherDetailResponse> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
    };

    const response = await fetch(
      getApiV1Url(`/projects/${projectCode}/vouchers/${voucherCode}`),
      {
        method: 'GET',
        headers
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result.data;
  } catch (error: any) {
    console.error('교환권 상세 조회 API 오류:', error);
    
    // 네트워크 에러인 경우 사용자 친화적 메시지로 변환
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }
    
    throw error;
  }
};

// 교환권 수정 API
export const updateVoucher = async (
  projectCode: string,
  voucherCode: string,
  voucherData: VoucherUpdateRequest
): Promise<VoucherUpdateResponse> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
    };

    const response = await fetch(
      getApiV1Url(`/projects/${projectCode}/vouchers/${voucherCode}`),
      {
        method: 'POST',
        headers,
        body: JSON.stringify(voucherData)
      }
    );

    if (!response.ok) {
      const statusCode = response.status;
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = CommonUtils.getHttpErrorMessage(statusCode, errorData);
      throw new Error(errorMessage);
    }

    const result = await response.json();
    return {
      success: true,
      message: result.message || '교환권이 성공적으로 수정되었습니다.',
      data: result.data
    };
  } catch (error: any) {
    console.error('교환권 수정 API 오류:', error);
    
    // 네트워크 에러인 경우 사용자 친화적 메시지로 변환
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }
    
    throw error;
  }
};

// 교환권 삭제 API
export const deleteVoucher = async (
  projectCode: string,
  voucherCode: string
): Promise<{ success: boolean; message: string }> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
    };

    const response = await fetch(
      getApiV1Url(`/projects/${projectCode}/vouchers/${voucherCode}`),
      {
        method: 'DELETE',
        headers
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return {
      success: true,
      message: '교환권이 성공적으로 삭제되었습니다.'
    };
  } catch (error: any) {
    console.error('교환권 삭제 API 오류:', error);
    
    // 네트워크 에러인 경우 사용자 친화적 메시지로 변환
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }
    
    throw error;
  }
};

// ============================================================================
// FILE APIs (대표 이미지)
// ============================================================================

export interface VoucherFileCreateRequest {
  type: string;
  sortOrder: string;
  tempKey: string;
  tempFileName: string;
  originalName: string;
  contentType: string;
  declaredFileSize: number;
}

const getVoucherApiHeaders = (): Record<string, string> => {
  const userInfo = getUserInfo();
  if (!userInfo) {
    throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
  }

  return {
    'Content-Type': 'application/json',
    'X-Account-Code': userInfo.accountCode,
    'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId,
  };
};

export const toVoucherPrimaryFileRequest = (
  uploadFile: UploadFilePayload
): VoucherFileCreateRequest => ({
  type: 'PRIMARY',
  sortOrder: generateAfter(null),
  tempKey: uploadFile.tempKey,
  tempFileName: uploadFile.tempFileName,
  originalName: uploadFile.originalName,
  contentType: uploadFile.contentType,
  declaredFileSize: uploadFile.declaredFileSize,
});

/** 교환권 대표 파일 썸네일 스토리지 URI */
export const getVoucherPrimaryThumbnailStorageUri = (
  projectCode: string,
  voucherCode: string
): string => `/files/${projectCode}/voucher/thumb/${voucherCode}.webp`;

/** 교환권 대표 파일 표시 URL (CDN 설정 시 CDN+?v= / 없으면 presign) */
export const resolveVoucherPrimaryUrl = async (
  projectCode: string,
  voucherCode: string,
  primaryVersion?: number | null,
  updatedAt?: string | number | null
): Promise<string | null> =>
  resolveStoredFileUrl(getVoucherPrimaryThumbnailStorageUri(projectCode, voucherCode), {
    cacheVersion: toCdnCacheVersion(primaryVersion, updatedAt),
  });

/** 교환권 파일 등록 */
export const createVoucherFile = async (
  projectCode: string,
  voucherCode: string,
  request: VoucherFileCreateRequest
): Promise<void> => {
  try {
    const url = getApiV1Url(`/projects/${projectCode}/vouchers/${voucherCode}/files`);
    console.log('[교환권 파일 등록] API 요청', { url, body: request });

    const response = await fetch(url, {
      method: 'POST',
      headers: getVoucherApiHeaders(),
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const statusCode = response.status;
      const errorData = await response.json().catch(() => ({}));
      throw new Error(CommonUtils.getHttpErrorMessage(statusCode, errorData));
    }

    const result = await response.json();
    console.log('교환권 파일 등록 성공 - 서버 응답:', result);
  } catch (error: unknown) {
    console.error('교환권 파일 등록 API 오류:', error);
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as Error & { isNetworkError?: boolean }).isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/** 교환권 대표 파일 삭제 */
export const deleteVoucherPrimary = async (
  projectCode: string,
  voucherCode: string
): Promise<void> => {
  try {
    const url = getApiV1Url(
      `/projects/${projectCode}/vouchers/${voucherCode}/files/primary/delete`
    );
    console.log('[교환권 대표 파일 삭제] API 요청', { url, method: 'POST' });

    const response = await fetch(url, {
      method: 'POST',
      headers: getVoucherApiHeaders(),
    });

    if (!response.ok) {
      const statusCode = response.status;
      const errorData = await response.json().catch(() => ({}));
      throw new Error(CommonUtils.getHttpErrorMessage(statusCode, errorData));
    }

    const result = await response.json();
    console.log('교환권 대표 파일 삭제 성공 - 서버 응답:', result);
  } catch (error: unknown) {
    console.error('교환권 대표 파일 삭제 API 오류:', error);
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as Error & { isNetworkError?: boolean }).isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};